import type { ShopSelectFragment } from '@/api/app/fragments/shop.generated';
import type { TSurveysShown } from '@/modules/survey/types';
import { defineStore } from 'pinia';

type ShopType = {
  isGemPagesV6: boolean;
  isGemPagesMixed: boolean;
  isGemPagesV7: boolean;
};
type ShopMetaType = {
  isFetchingDataSurvey: boolean;
  dataSurveyShown: TSurveysShown[];
  dropElementCount: number;
  isPreviewed: boolean;
};

const IS_GEMPAGES_V6_VALUE = 'false';
const IS_GEMPAGES_V7_VALUE = 'true';
const IS_GEMPAGES_MIXED_VALUE = 'mixed';

export type State = {
  connectedType: 'storefront' | 'instant' | 'gempagesv7' | 'editor';
  shop: ShopSelectFragment | undefined;
  isAdminLogin?: boolean;
  shopId?: string;
  token?: string;
  shopDomain?: string;
  userHash?: string;
  status?: boolean;
  firstName?: string;
  isGemPagesV7?: string;
  shopType: ShopType;
  shopMeta: ShopMetaType;
  isLatestVersion: boolean;
  isUpgradingVersion: boolean;
};

export const useShopStore = defineStore('shop', {
  state: (): State => ({
    connectedType: 'storefront',
    isAdminLogin: false,
    shop: undefined,
    shopId: undefined,
    token: undefined,
    shopDomain: undefined,
    userHash: undefined,
    status: false,
    firstName: undefined,
    isGemPagesV7: 'true',
    shopType: {
      isGemPagesV6: false,
      isGemPagesMixed: false,
      isGemPagesV7: false,
    },
    shopMeta: {
      isFetchingDataSurvey: false,
      dataSurveyShown: [],
      dropElementCount: 0,
      isPreviewed: false,
    },
    isLatestVersion: false,
    isUpgradingVersion: false,
  }),
  getters: {
    getConnectedType(state: State) {
      return state.connectedType;
    },
    getIsAdminLogin(state: State) {
      return state.isAdminLogin;
    },
    getShopId(state: State) {
      return state.shopId;
    },
    getToken(state: State) {
      return state.token;
    },
    getUserHash(state: State) {
      return state.userHash;
    },
    getStatus(state: State) {
      return state.status;
    },
    getShop(state: State) {
      return state.shop;
    },
    getFirstName(state: State) {
      return state.firstName;
    },
    getShopDomain(state: State) {
      return state.shopDomain;
    },
    getIsGemPagesV6(state: State) {
      return state.shopType.isGemPagesV6;
    },
    getIsGemPagesMixed(state: State) {
      return state.shopType.isGemPagesMixed;
    },
    getIsGemPagesV7(state: State) {
      return state.shopType.isGemPagesV7;
    },
    getIsFetchingDataSurvey(state) {
      return state.shopMeta.isFetchingDataSurvey;
    },
    getDataSurveyShown(state) {
      return state.shopMeta.dataSurveyShown;
    },
    getDropElementCount(state) {
      return state.shopMeta.dropElementCount;
    },
    getIsPreviewed(state) {
      return state.shopMeta.isPreviewed;
    },
    getIsLatestVersion(state) {
      return state.isLatestVersion;
    },
    getIsUpgradingVersion(state) {
      return state.isUpgradingVersion;
    },
  },
  actions: {
    setConnectedType(data: 'storefront' | 'instant' | 'gempagesv7' | 'editor') {
      this.connectedType = data;
    },
    setIsAdminLogin(data: boolean) {
      this.isAdminLogin = data;
    },
    setShop(data: ShopSelectFragment | undefined) {
      this.shop = data;
    },
    setShopDomain(data: string | undefined) {
      this.shopDomain = data;
    },
    setShopId(data: string | undefined) {
      this.shopId = data;
    },
    setToken(data: string | undefined) {
      this.token = data;
    },
    setUserHash(data: string | undefined) {
      this.userHash = data;
    },
    setStatus(status: boolean) {
      this.status = status;
    },
    setFirstName(firstName: string | undefined) {
      this.firstName = firstName;
    },
    setIsGemPagesV7(isGemPagesV7: string | undefined) {
      this.isGemPagesV7 = isGemPagesV7;
    },
    setShopType(isGemPagesV7: string | undefined) {
      this.shopType.isGemPagesV6 = isGemPagesV7 == IS_GEMPAGES_V6_VALUE;
      this.shopType.isGemPagesMixed = isGemPagesV7 == IS_GEMPAGES_MIXED_VALUE;
      this.shopType.isGemPagesV7 = isGemPagesV7 == IS_GEMPAGES_V7_VALUE;
    },
    setIsFetchingDataSurvey(payload: boolean) {
      this.shopMeta.isFetchingDataSurvey = payload;
    },
    setDataSurveyShown(payload: TSurveysShown[]) {
      this.shopMeta.dataSurveyShown = payload;
    },
    setDropElementCount(payload: number) {
      this.shopMeta.dropElementCount = payload;
    },
    setIsPreviewed() {
      this.shopMeta.isPreviewed = true;
    },
    setIsLatestVersion(value: boolean) {
      this.isLatestVersion = value;
    },
    setIsUpgradingVersion(value: boolean) {
      this.isUpgradingVersion = value;
    },
  },
});

export default useShopStore;
